import { useParams, useOutletContext } from "react-router-dom"
import { useComputed, useSignal } from "@preact/signals-react"

import Button from 'react-bootstrap/Button'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row'

import NewMerchantForm from "./NewMerchantForm"
import NewOrganizationForm from "./NewOrganizationForm"

import { insertMerchant, insertOrganization, validateNewBEAdminEmail } from '../../../../state/actions/businessEntityActions'

import { BusinessEntityType, OrganizationType } from "../../../../utils/constants.js"
import { assembleEntity } from "../../../../utils/form.js"

const NewBusinessEntityForm = function ({ parentBusinessEntity, cancelAddBE, selectedBusinessEntityTypeSignal, currencies, businessEntityTypeStringComputed, refetchBusinessEntity, errorSignal }) {

	const { beId } = useParams()
	const { user, config, accessToken } = useOutletContext()

	const validatedSignal = useSignal(false)
	const adminEmailErrorSignal = useSignal(null)
	const emailMatchErrorSignal = useSignal(null)
	const isSubmittingSignal = useSignal(false)

	const updateSelectedBusinessEntityType = function (event) {
		selectedBusinessEntityTypeSignal.value = parseInt(event.target.value)
	}

	const SelectOptions = function () {
		let orgTypes = []
		if (parentBusinessEntity.isStandardOrganization || parentBusinessEntity.isRootOrganization) {
			orgTypes.push({ key: 'Organization', value: BusinessEntityType.Organization });
		}
		if (parentBusinessEntity.isResellerOrganization) {
			orgTypes.push({ key: 'Reseller', value: BusinessEntityType.Reseller });
		}
		let options = [(<option key='' value=''>Select Organization Type</option>)]
		for (let i = 0; i < orgTypes.length; i++) {
			const content = orgTypes[i]
			options.push(<option key={content.key} value={content.value}>{content.key}</option>)
		}
		return options
	}

	const actionAttributeComputed = useComputed(
		() => {
			let action
			const type = selectedBusinessEntityTypeSignal.value
			switch (type) {
				case BusinessEntityType.Merchant:
					action = "merchants"
					break
				case BusinessEntityType.Organization:
					action = "organizations"
					break
				case BusinessEntityType.Reseller:
					action = "resellers"
					break
				default:
					action = "error"
					break
			}
			return `${config.accountApiUrl}/${action}`
		}
	)

	const validateAdminEmail = async (emailAddress) => {
		return await validateNewBEAdminEmail(config, beId, emailAddress, accessToken)
	}

	const assembleMerchant = (form) => {
		return assembleEntity(form)
	}

	const assembleOrganization = (form) => {
		const entity = assembleEntity(form)
		entity.baseImageUrl = null
		entity.businessEntity = null
		entity.groups = []
		entity.guid = null
		entity.isActive = true
		entity.isRoot = false
		entity.merchantsAllowed = 0
		entity.organizationTypeId = selectedBusinessEntityTypeSignal.value === BusinessEntityType.Reseller
			? OrganizationType.Reseller
			: OrganizationType.Standard
		entity.properties = []
		entity.roles = []
		entity.type = BusinessEntityType.OrganizationUnit
		return entity
	}

	const submitNewBEForm = async (event) => {
		event.preventDefault()
		validatedSignal.value = true

		const form = event.target
		if (form.checkValidity() === false) {
			return
		}
		if (adminEmailErrorSignal.value || emailMatchErrorSignal.value) {
			return
		}

		isSubmittingSignal.value = true
		try {
			if (selectedBusinessEntityTypeSignal.value === BusinessEntityType.Merchant) {
				await insertMerchant(assembleMerchant(form), config, accessToken)
			} else {
				await insertOrganization(assembleOrganization(form), config, accessToken)
			}
			cancelAddBE()
		} catch (error) {
			errorSignal.value = error.message || error.toString()
			cancelAddBE()
		} finally {
			isSubmittingSignal.value = false
		}
	}

	return (
		<Form method="POST" id="newBEForm" action={actionAttributeComputed.value} noValidate validated={validatedSignal.value} onSubmit={submitNewBEForm}>
			<Row className="mb-3">
				<Form.Group as={Col}>
					<Form.Label>Parent</Form.Label>
					<Form.Control value={parentBusinessEntity.name} disabled />
				</Form.Group>
			</Row>
			<Row className="mb-3">
				<Form.Group as={Col}>
					<Form.Label>Type</Form.Label>
					<Form.Select name="type" onChange={updateSelectedBusinessEntityType} disabled={parentBusinessEntity.isEditDisabled} required value={selectedBusinessEntityTypeSignal.value ? selectedBusinessEntityTypeSignal.value : ''}>
						<SelectOptions></SelectOptions>
					</Form.Select>
					<Form.Control.Feedback type="invalid">Please select an organization type.</Form.Control.Feedback>
				</Form.Group>
			</Row>
			{
				selectedBusinessEntityTypeSignal.value == BusinessEntityType.Merchant && (
					<NewMerchantForm currencies={currencies}></NewMerchantForm>
				)
			}
			{
				selectedBusinessEntityTypeSignal.value == BusinessEntityType.Organization && (
					<NewOrganizationForm
						adminEmailErrorSignal={adminEmailErrorSignal}
						emailMatchErrorSignal={emailMatchErrorSignal}
						validatedSignal={validatedSignal}
						validateAdminEmail={validateAdminEmail}
					></NewOrganizationForm>
				)
			}
			{
				selectedBusinessEntityTypeSignal.value == BusinessEntityType.Reseller && (
					<NewOrganizationForm
						adminEmailErrorSignal={adminEmailErrorSignal}
						emailMatchErrorSignal={emailMatchErrorSignal}
						validatedSignal={validatedSignal}
						validateAdminEmail={validateAdminEmail}
						isResellerSelected={true}
					></NewOrganizationForm>
				)
			}
			<div className="d-flex justify-content-end gap-2 mt-3">
				<Button variant="secondary" onClick={cancelAddBE}>Cancel</Button>
				<Button variant="primary" type="submit" disabled={isSubmittingSignal.value}>
					{isSubmittingSignal.value ? 'Submitting...' : 'Add'}
				</Button>
			</div>
			<input type="hidden" name="organizationTypeId" value={OrganizationType.Standard} />
			<input type="hidden" name="parentBusinessEntityId" value={beId} />
		</Form>
	)
}
export default NewBusinessEntityForm
